import React, { useCallback, useEffect, useState } from 'react';
import { CircularProgress, LinearProgress, Typography } from '@material-ui/core';
import axios from 'axios';
import { API_BASE_URL } from '@config';

// How often to ask whether the cluster is up. Slow enough not to hammer the
// API with a room full of students, quick enough that nobody sits looking at a
// ready cluster thinking it is still starting.
const POLL_MS = 10000;

/**
 * "Your workshop is starting."
 *
 * Shown to a participant who has signed in successfully but whose cluster was
 * asleep and is now coming back. A cloud cluster takes minutes, and without
 * this the student is dropped onto something that is not up yet and reasonably
 * concludes the workshop is broken.
 *
 * The estimate is the cluster's own measured last start where Heidi has one,
 * so it reflects what this cluster on this provider really does rather than a
 * number somebody picked. It counts down and never claims to be finished while
 * the server still says otherwise.
 */
export default function ClusterStartingNotice({ startup }) {
    const initial = Math.max(30, (startup && startup.etaSeconds) || 300);
    const [remaining, setRemaining] = useState(initial);
    const [ready, setReady] = useState(false);
    const [overrun, setOverrun] = useState(false);

    const names = ((startup && startup.starting) || []).map(s => s.name).filter(Boolean);
    const measured = ((startup && startup.starting) || []).some(s => s.measured);

    // Tick the countdown down. Purely cosmetic: readiness comes from the
    // server, never from this clock reaching zero.
    useEffect(() => {
        const handle = setInterval(() => {
            setRemaining(prev => {
                if (prev <= 1) { setOverrun(true); return 0; }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(handle);
    }, []);

    const check = useCallback(async () => {
        try {
            const res = await axios.get(`${API_BASE_URL}/api/trainings/participant/cluster-startup`);
            if (res.data && res.data.ready) {
                setReady(true);
                // Straight into the workshop - the participant is already
                // signed in, this screen was only ever the wait.
                window.location.reload();
                return;
            }
            if (res.data && typeof res.data.etaSeconds === 'number' && res.data.etaSeconds > 0) {
                setRemaining(Math.max(15, res.data.etaSeconds));
                setOverrun(false);
            }
        } catch (e) {
            // A failed check is not a failed start. Keep waiting and try again.
        }
    }, []);

    useEffect(() => {
        const handle = setInterval(check, POLL_MS);
        return () => clearInterval(handle);
    }, [check]);

    const minutes = Math.max(1, Math.ceil(remaining / 60));

    return (
        <div className="w-full flex flex-col items-center text-center" style={{ paddingTop: 24 }}>
            <CircularProgress size={48} style={{ marginBottom: 24 }} />

            <Typography variant="h6" style={{ marginBottom: 8 }}>
                {ready ? 'Your workshop is ready' : 'Your workshop is starting'}
            </Typography>

            <Typography variant="body2" color="textSecondary" style={{ maxWidth: 420, marginBottom: 16 }}>
                {names.length > 0
                    ? `${names.join(', ')} ${names.length > 1 ? 'were' : 'was'} shut down while unused and ${names.length > 1 ? 'are' : 'is'} now starting.`
                    : 'Your cluster was shut down while unused and is now starting.'}
            </Typography>

            <div style={{ width: '100%', maxWidth: 420, marginBottom: 12 }}>
                <LinearProgress
                    variant={overrun ? 'indeterminate' : 'determinate'}
                    value={Math.min(100, Math.max(0, ((initial - remaining) / initial) * 100))}
                />
            </div>

            {/* Once past the estimate, stop counting and say so. A countdown
                that sits at "0 minutes" while nothing happens reads as broken,
                and a start can legitimately run long. */}
            <Typography variant="body2" color="textSecondary">
                {overrun
                    ? 'Taking longer than usual. Still starting.'
                    : measured
                        ? `About ${minutes} minute${minutes === 1 ? '' : 's'} remaining.`
                        : `Typically around ${minutes} minute${minutes === 1 ? '' : 's'}.`}
            </Typography>

            <Typography variant="caption" color="textSecondary" style={{ marginTop: 16, maxWidth: 420 }}>
                You are signed in. This page continues automatically when the workshop is ready.
            </Typography>
        </div>
    );
}
